<template>
  <div class="loader-container" :class="{ 'fade-out': !isLoading }">
    <div class="loader">
      <img
        src="@/assets/images/logo-white.avif"
        :alt="logoAlt"
        class="loader-logo"
        width="200"
        height="95"
        decoding="async"
        loading="eager"
        fetchpriority="high"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import brand from '@/config/brand';
import { loadSiteContactPublic } from '@/composables/useSiteContact';
import { getStoreName } from '@/utils/siteTitle';

const isLoading = ref(true);
const logoAlt = ref(`${brand.storeName} Logo`);
let handleWindowLoad = null;
let maxDelayTimer = null;

const hideLoader = () => {
  isLoading.value = false;
};

const MIN_DISPLAY_MS = 500;
const MAX_DELAY_MS = 3000;
const mountedAt = Date.now();

const hideLoaderSafely = () => {
  // El loader nunca debe quedar colgado: respeta un mínimo de display
  // (evita flash) y un cap duro de 3s (imágenes remotas/red lenta).
  const elapsed = Date.now() - mountedAt;
  const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);
  setTimeout(hideLoader, remaining);
};

const onDataReady = () => hideLoaderSafely();

onMounted(() => {
  loadSiteContactPublic().finally(() => {
    logoAlt.value = `${getStoreName()} Logo`;
  });

  // Cap duro: pase lo que pase, el loader se oculta a los 3s.
  maxDelayTimer = setTimeout(hideLoaderSafely, MAX_DELAY_MS);

  // Si la página ya pintó contenido principal, liberar apenas el min display.
  document.addEventListener('app:initial-data-ready', onDataReady);

  if (document.readyState === 'complete') {
    requestAnimationFrame(hideLoaderSafely);
    return;
  }

  handleWindowLoad = () => hideLoaderSafely();
  window.addEventListener('load', handleWindowLoad, { once: true });
});

onBeforeUnmount(() => {
  if (handleWindowLoad) {
    window.removeEventListener('load', handleWindowLoad);
    handleWindowLoad = null;
  }
  document.removeEventListener('app:initial-data-ready', onDataReady);
  if (maxDelayTimer) {
    clearTimeout(maxDelayTimer);
    maxDelayTimer = null;
  }
});
</script>

<script>
  export default{
    name: 'AppLoader',
  }
</script>

<style scoped>
.loader-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: var(--white-color);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  transition: opacity 0.5s, visibility 0.5s;
}

.fade-out {
  opacity: 0;
  visibility: hidden;
}

.loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.loader-logo {
  width: 200px;
  height: auto;
  animation: pulse 1s infinite;
}


.progress {
  width: 100%;
  height: 100%;
  background: var(--primary-color);
  animation: loading 1s ease infinite;
}

@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}

</style>
